import { ok } from '@shared/utils';
import type { AppError, AsyncResult } from '@shared/utils';
import type { PackagePostBootstrapTask } from '@shared/core';
import { Logger } from '../../classes/Logger';
import type { LoggerRuntimeKind } from '../types/LoggerRuntimeKind';
import { UNHANDLED_ERROR_TYPES } from '../../constants/UNHANDLED_ERROR_TYPES';

/**
 * @group Logger Bootstrap
 * @summary
 * <blockquote>Post-bootstrap task that attaches global error handlers (`error`, `unhandledrejection`,
 * `uncaughtException`, `unhandledRejection`) for the current runtime so unhandled
 * exceptions are automatically forwarded to Logger.</blockquote>
 *
 * @remarks
 * Handlers are installed once per runtime and guarded by a `globalThis` flag to
 * prevent duplicate registration across hot-reloads.
 *
 * @example
 * ```typescript
 * import { LoggerGlobalErrorHandlersPostBootstrapTask } from '@shared/logger';
 *
 * const task = new LoggerGlobalErrorHandlersPostBootstrapTask('browser');
 * await task.run(); // installs window.addEventListener('error', ...)
 * ```
 */
export class LoggerGlobalErrorHandlersPostBootstrapTask implements PackagePostBootstrapTask {
  readonly id = 'logger-post-bootstrap-global-error-handlers';

  constructor(private readonly runtimeKind: LoggerRuntimeKind) {}

  private setupBrowserErrorHandlers(): void {
    const browserGlobal = globalThis as typeof globalThis & {
      addEventListener?: (eventName: string, handler: (event: any) => void) => void;
      __loggerBrowserGlobalErrorHandlersInitialized?: boolean;
    };

    if (browserGlobal.__loggerBrowserGlobalErrorHandlersInitialized) {
      return;
    }

    browserGlobal.addEventListener?.('error', (event: any) => {
      void Logger.error(
        'Global Error Event',
        {
          errorType: UNHANDLED_ERROR_TYPES.ERROR_EVENT,
          message: String(event?.message || ''),
          filename: String(event?.filename || ''),
          lineno: Number(event?.lineno || 0),
          colno: Number(event?.colno || 0),
        },
        event?.error instanceof Error ? event.error : undefined
      );
    });

    browserGlobal.addEventListener?.('unhandledrejection', (event: any) => {
      const error = event?.reason instanceof Error ? event.reason : new Error(String(event?.reason));
      void Logger.error(
        'Unhandled Promise Rejection',
        {
          errorType: UNHANDLED_ERROR_TYPES.UNHANDLED_REJECTION,
          reason: String(event?.reason),
        },
        error
      );
    });

    browserGlobal.__loggerBrowserGlobalErrorHandlersInitialized = true;
  }

  private setupServerErrorHandlers(): void {
    const globalForNodeHandlers = globalThis as typeof globalThis & {
      process?: {
        on?: (eventName: string, handler: (...args: unknown[]) => void) => void;
      };
      __loggerNodeGlobalErrorHandlersInitialized?: boolean;
    };

    if (globalForNodeHandlers.__loggerNodeGlobalErrorHandlersInitialized) {
      return;
    }

    globalForNodeHandlers.process?.on?.('uncaughtException', (error: unknown) => {
      const exceptionError = error instanceof Error ? error : new Error(String(error));
      void Logger.error(
        'Uncaught Exception',
        {
          errorType: UNHANDLED_ERROR_TYPES.UNCAUGHT_EXCEPTION,
          errorMessage: exceptionError.message,
          stack: exceptionError.stack,
        },
        exceptionError
      );
    });

    globalForNodeHandlers.process?.on?.('unhandledRejection', (reason: unknown) => {
      const error = reason instanceof Error ? reason : new Error(String(reason));
      void Logger.error(
        'Unhandled Promise Rejection',
        {
          errorType: UNHANDLED_ERROR_TYPES.UNHANDLED_REJECTION,
          reason: String(reason),
        },
        error
      );
    });

    globalForNodeHandlers.__loggerNodeGlobalErrorHandlersInitialized = true;
  }

  private setupMobileErrorHandlers(): void {
    const globalForMobileHandlers = globalThis as typeof globalThis & {
      __loggerMobileGlobalErrorHandlersInitialized?: boolean;
      ErrorUtils?: {
        getGlobalHandler: () => (error: Error, isFatal?: boolean) => void;
        setGlobalHandler: (handler: (error: Error, isFatal?: boolean) => void) => void;
      };
      __onUnhandledRejection?: (id: string, error: Error) => void;
    };

    if (globalForMobileHandlers.__loggerMobileGlobalErrorHandlersInitialized) {
      return;
    }

    if (globalForMobileHandlers.ErrorUtils) {
      const originalHandler = globalForMobileHandlers.ErrorUtils.getGlobalHandler();
      globalForMobileHandlers.ErrorUtils.setGlobalHandler((error: Error, isFatal?: boolean) => {
        void Logger.error(
          'Uncaught React Native Error',
          {
            errorType: UNHANDLED_ERROR_TYPES.UNCAUGHT_EXCEPTION,
            errorMessage: error.message,
            stack: error.stack,
            isFatal: Boolean(isFatal),
          },
          error
        );
        originalHandler(error, isFatal);
      });
    }

    const originalUnhandledRejection = globalForMobileHandlers.__onUnhandledRejection;
    globalForMobileHandlers.__onUnhandledRejection = (id: string, error: Error) => {
      void Logger.error(
        'Unhandled Promise Rejection (React Native)',
        {
          errorType: UNHANDLED_ERROR_TYPES.UNHANDLED_REJECTION,
          rejectionId: id,
          errorMessage: error.message,
          stack: error.stack,
        },
        error
      );
      originalUnhandledRejection?.(id, error);
    };

    globalForMobileHandlers.__loggerMobileGlobalErrorHandlersInitialized = true;
  }

  async run(): AsyncResult<void, AppError> {
    if (this.runtimeKind === 'browser') {
      this.setupBrowserErrorHandlers();
      return ok(undefined);
    }

    if (this.runtimeKind === 'server') {
      this.setupServerErrorHandlers();
      return ok(undefined);
    }

    this.setupMobileErrorHandlers();
    return ok(undefined);
  }
}
